import { hasFilters, type InspectionFilters } from '@lib/inspections/filters';
import { useLocalization } from '@lib/localization';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useGetInspectionFacetsQuery } from '@store/api/inspectionEndpoints';
import { folderPathOf, useGetProjectsQuery } from '@store/api/projectEndpoints';
import { Folder, FunnelX, User, Users } from 'lucide-react';
import { useMemo } from 'react';
import DateRangeFilter from './DateRangeFilter';
import FacetFilter, { type FacetOption } from './FacetFilter';
import SearchField from './SearchField';

interface InspectionFilterBarProps {
  filters: InspectionFilters;
  onChange: (filters: InspectionFilters) => void;
  /** Clears every filter: the toolbar's button and the page's empty state call the same one. */
  onClear: () => void;
  /** How many times every filter was cleared. The search box is keyed by it, so a clear mounts it anew. */
  clears: number;
  /** How many inspections the list holds under the filters, for the search box. */
  count: number;
}

const byLabel = (a: FacetOption, b: FacetOption) => a.label.localeCompare(b.label);

/** The narrowest the search box gets before the pills go down a line. */
const SEARCH_LEAST_WIDTH = 180;

/**
 * The inspection list's filters, as the Projects page lays out its toolbar: the name search
 * on the left, the rest on the right. Every filter that is set narrows the list further —
 * they are joined with AND — while the values inside one pill are alternatives.
 *
 * The projects are the ones the reader may open (`GET /v1/projects`). The people and the
 * teams come from the rows the reader can read, over all of them and not the page on screen
 * (`GET /v1/inspections/facets`): the authors, and the teams owning their projects. Taken
 * from the reader's own teams instead, they left out the authors and the owning team of
 * inspections in folders another team shared, uploaded by someone outside the team or by a
 * member who has left, and offered an admin everyone, most with nothing in the list. A
 * combination of choices can still find nothing; the list then says so.
 *
 * The toolbar wraps at every width, but only once the search box has given way to its least
 * width, so it stays one line wherever it fits and setting a filter does not push the list
 * down there. Where it does not fit, the pills go down a line, and wrap among themselves
 * on a phone, rather than run off the screen.
 */
const InspectionFilterBar: React.FC<InspectionFilterBarProps> = ({ filters, onChange, onClear, clears, count }) => {
  const { t } = useLocalization();

  const { data: projects, isLoading: projectsLoading } = useGetProjectsQuery({});
  const { data: facets, isLoading: facetsLoading } = useGetInspectionFacetsQuery();

  const set = <K extends keyof InspectionFilters>(key: K, value: InspectionFilters[K]) =>
    onChange({ ...filters, [key]: value });

  /**
   * Projects are listed under the root folder they sit in — a team's root carries the team's
   * name — and only where there is more than one: a heading over the whole list says nothing.
   */
  const projectOptions = useMemo((): FacetOption[] => {
    const listed = (projects ?? []).map((project) => ({
      id: project.projectInfo.id,
      label: project.projectInfo.name,
      root: folderPathOf(project).split('/').filter(Boolean)[0] ?? '',
    }));

    const grouped = new Set(listed.map((project) => project.root)).size > 1;

    return listed
      .sort((a, b) => (grouped ? a.root.localeCompare(b.root) : 0) || a.label.localeCompare(b.label))
      .map(({ root, ...option }) => ({ ...option, group: grouped ? root : undefined }));
  }, [projects]);

  const userOptions = useMemo((): FacetOption[] => (facets?.users ?? []).map(({ id, name, username }) => ({
    id,
    label: name || username,
    secondary: name ? username : undefined,
    avatar: true,
  })).sort(byLabel), [facets]);

  const teamOptions = useMemo(
    (): FacetOption[] => (facets?.teams ?? []).map(({ id, name }) => ({ id, label: name })).sort(byLabel),
    [facets]
  );

  const filtered = hasFilters(filters);

  return (
    <Box
      role="search"
      aria-label={t('Inspections_Filter_Label')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: '12px',
        flexWrap: 'wrap',
      }}
    >
      {/* Its basis is its least width: a line is broken by what its items ask for before they
          grow, so a basis of 400px sent the pills down while the box could still have given
          way. It grows to the 400px the other pages' search box has, and across a line of its
          own on a phone. */}
      <SearchField
        key={clears}
        value={filters.search}
        onCommit={(search) => set('search', search)}
        placeholder={t('Project_Inspections_Search')}
        count={count}
        sx={{
          width: 'auto', flex: `1 1 ${SEARCH_LEAST_WIDTH}px`, minWidth: SEARCH_LEAST_WIDTH, maxWidth: { xs: '100%', sm: 400 },
        }}
      />

      {/* Free to shrink, so that its own wrapping applies once it has a line to itself and that
          line is still too short. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
        <FacetFilter
          label={t('Common_Projects')}
          icon={<Folder size={15} />}
          options={projectOptions}
          selected={filters.projectIds}
          onChange={(ids) => set('projectIds', ids)}
          searchPlaceholder={t('Inspections_Filter_FindProject')}
          loading={projectsLoading}
        />

        <FacetFilter
          label={t('Common_Users')}
          icon={<User size={15} />}
          options={userOptions}
          selected={filters.userIds}
          onChange={(ids) => set('userIds', ids)}
          searchPlaceholder={t('Team_AddMembers_Search')}
          loading={facetsLoading}
        />

        <FacetFilter
          label={t('Common_Teams')}
          icon={<Users size={15} />}
          options={teamOptions}
          selected={filters.teamIds}
          onChange={(ids) => set('teamIds', ids)}
          searchPlaceholder={t('Team_Search')}
          loading={facetsLoading}
        />

        <DateRangeFilter value={filters.date} onChange={(date) => set('date', date)} />

        {/* Always there and only enabled while something is set: one that came and went would
            move the pills beside it each time. */}
        <Tooltip title={t('Inspections_Filter_Reset')}>
          <span>
            <IconButton
              size="small"
              disabled={!filtered}
              onClick={onClear}
              aria-label={t('Inspections_Filter_Reset')}
            >
              <FunnelX size={16} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default InspectionFilterBar;
